import { useEffect, useState } from 'react';
import {
  DropIcon,
  FanIcon,
  FlameIcon,
  PowerIcon,
  SnowflakeIcon,
  ThermometerIcon,
  WifiSlashIcon,
  type Icon,
} from '@phosphor-icons/react';
import { apiFetch } from '../../../auth/apiFetch';
import { pollWithWake } from '../../../data/kioskRefresh';

// The thermostat as both the kiosk dial (EcobeeWidget) and the home screen's
// Thermostat card read it: one HA entity polled through the /api/ha proxy,
// plus the running-state inference the Nest needs. Read-only — nothing here
// changes the thermostat.

export const THERMOSTAT_ENTITY_ID = 'climate.thermostat';
const REFRESH_MS = 60 * 1000;

export interface ThermostatState {
  entity_id: string;
  state: string;
  attributes: {
    current_temperature?: number;
    target_temp_low?: number;
    target_temp_high?: number;
    temperature?: number | null;
    hvac_action?: HvacAction;
    friendly_name?: string;
  };
}

interface HaResponse {
  states: Record<string, ThermostatState>;
}

export type HvacAction = 'heating' | 'cooling' | 'idle' | 'fan' | 'off' | 'drying';

export interface ActionStyle {
  label: string;
  Icon: Icon;
  colorVar: string;
  /** False for idle / off — the arc renders static and dim. */
  active: boolean;
  /** CSS variable for the in-card radial wash. Omitted for muted states
   *  (idle / off / fan-only) so the card sits flat with no tint. */
  softVar?: string;
}

// HA marks the entity `unavailable` / `unknown` when it loses contact with
// the thermostat (integration down, device off Wi-Fi). Every attribute is
// null in that state, so without an explicit branch the widget would fall
// through to the Idle default and silently mask the outage — rose styling
// makes the fault visible from across the kitchen.
export const OFFLINE_STYLE: ActionStyle = {
  label: 'Offline',
  Icon: WifiSlashIcon,
  colorVar: '--berg-rose-light',
  active: false,
  softVar: '--berg-rose-soft',
};

export function isEntityUnavailable(state: string): boolean {
  return state === 'unavailable' || state === 'unknown';
}

// The Nest (connected via Matter, 2026-09) never reports hvac_action — the
// ecobee did. Without a fallback the dial would show "Idle" forever, even
// mid-cool. Infer the running state from the mode (entity state) and the
// current-vs-target delta; the 0.5° margin keeps the label from flapping
// around the setpoint.
export function inferHvacAction(
  state: string,
  attrs: ThermostatState['attributes']
): HvacAction | undefined {
  if (state === 'off') return 'off';
  const cur = attrs.current_temperature;
  if (typeof cur !== 'number') return undefined;
  const target = attrs.temperature;
  const low = attrs.target_temp_low;
  const high = attrs.target_temp_high;
  if (state === 'cool' && typeof target === 'number') {
    return cur > target + 0.5 ? 'cooling' : 'idle';
  }
  if (state === 'heat' && typeof target === 'number') {
    return cur < target - 0.5 ? 'heating' : 'idle';
  }
  if (state === 'heat_cool' && typeof low === 'number' && typeof high === 'number') {
    if (cur > high + 0.5) return 'cooling';
    if (cur < low - 0.5) return 'heating';
    return 'idle';
  }
  return undefined;
}

export function styleForAction(action: HvacAction | undefined): ActionStyle {
  switch (action) {
    case 'heating':
      return {
        label: 'Heating',
        Icon: FlameIcon,
        colorVar: '--berg-red',
        active: true,
        softVar: '--berg-red-soft',
      };
    case 'cooling':
      return {
        label: 'Cooling',
        Icon: SnowflakeIcon,
        colorVar: '--berg-blue',
        active: true,
        softVar: '--berg-blue-soft',
      };
    case 'fan':
      return {
        label: 'Fan only',
        Icon: FanIcon,
        colorVar: '--berg-text-tertiary',
        active: true,
        softVar: '--berg-neutral-ghost',
      };
    case 'drying':
      return {
        label: 'Drying',
        Icon: DropIcon,
        colorVar: '--berg-amber-light',
        active: true,
        softVar: '--berg-amber-soft',
      };
    case 'off':
      return { label: 'Off', Icon: PowerIcon, colorVar: '--berg-text-tertiary', active: false };
    case 'idle':
    default:
      return {
        label: 'Idle',
        Icon: ThermometerIcon,
        colorVar: '--berg-text-tertiary',
        active: false,
        softVar: '--berg-neutral-ghost',
      };
  }
}

// The running action the UI should show: reported if the thermostat reports
// one, inferred otherwise. Undefined when the entity is offline.
export function thermostatAction(t: ThermostatState): HvacAction | undefined {
  if (isEntityUnavailable(t.state)) return undefined;
  return t.attributes.hvac_action ?? inferHvacAction(t.state, t.attributes);
}

// The setpoint the thermostat is working toward right now. In heat_cool mode
// that's the low end while heating and the high end while cooling; idle in
// heat_cool has no single target, so it's null and callers show the range.
export function activeSetpoint(t: ThermostatState, action: HvacAction | undefined): number | null {
  const { temperature, target_temp_low: low, target_temp_high: high } = t.attributes;
  if (typeof temperature === 'number') return temperature;
  if (action === 'heating' && typeof low === 'number') return low;
  if (action === 'cooling' && typeof high === 'number') return high;
  return null;
}

// Polls the thermostat entity. Pair a phone's use with pauseWhenHidden — the
// kiosk is a wall panel on mains power, a phone isn't — and pollWithWake
// re-ticks the moment the app comes back to the foreground anyway.
export function useThermostat({
  pollMs = REFRESH_MS,
  pauseWhenHidden = false,
}: { pollMs?: number; pauseWhenHidden?: boolean } = {}): {
  thermostat: ThermostatState | undefined;
  error: string | null;
} {
  const [data, setData] = useState<HaResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    async function tick() {
      if (pauseWhenHidden && document.hidden) return;
      try {
        const res = await apiFetch(`/api/ha?entities=${encodeURIComponent(THERMOSTAT_ENTITY_ID)}`);
        if (!alive) return;
        if (!res.ok) {
          setError(`ha ${res.status}`);
          return;
        }
        const json = (await res.json()) as HaResponse;
        setData(json);
        setError(null);
      } catch (e) {
        if (alive) setError(e instanceof Error ? e.message : 'fetch failed');
      }
    }
    void tick();
    const stop = pollWithWake(tick, pollMs);
    return () => {
      alive = false;
      stop();
    };
  }, [pollMs, pauseWhenHidden]);

  return { thermostat: data?.states[THERMOSTAT_ENTITY_ID], error };
}
